/**
 * Master Admin Assistance Banner
 *
 * Displays a prominent banner when admin is helping a user with their account.
 * Shows different messages based on permissions (assisted purchase vs view-only).
 */

import React, { useState, useEffect } from 'react'
import { getMasterAdminViewSession, exitMasterAdminView } from '../../lib/masterAdminAccess'

export const MasterAdminViewBanner: React.FC = () => {
  const [session, setSession] = useState<any>(null)
  const [duration, setDuration] = useState<string>('0:00')

  useEffect(() => {
    const sessionData = getMasterAdminViewSession()
    setSession(sessionData)

    if (sessionData) {
      // Update duration every second
      const interval = setInterval(() => {
        const start = new Date(sessionData.startTime).getTime()
        const now = new Date().getTime()
        const seconds = Math.floor((now - start) / 1000)
        const minutes = Math.floor(seconds / 60)
        const remainingSeconds = seconds % 60
        setDuration(`${minutes}:${remainingSeconds.toString().padStart(2, '0')}`)
      }, 1000)

      return () => clearInterval(interval)
    }
  }, [])

  if (!session) return null

  const handleExit = async () => {
    if (confirm('Exit admin assistance mode and return to admin dashboard?')) {
      await exitMasterAdminView()
      window.location.href = '/admin'
    }
  }

  // Determine mode and styling
  const isAssistedPurchase = session.mode === 'assisted_purchase' || session.permissions?.canPurchase
  const username = session.targetUsername || session.targetEmail

  // Different colors for different modes
  const bgColor = isAssistedPurchase ? '#2563EB' : '#DC2626' // Blue for assistance, Red for view-only
  const borderColor = isAssistedPurchase ? '#1E40AF' : '#991B1B'
  const icon = isAssistedPurchase ? '🤝' : '🔒'
  const title = isAssistedPurchase
    ? `ADMIN ASSISTANCE MODE - Helping ${username}`
    : 'ADMIN VIEW MODE - READ ONLY'
  const statusMessage = isAssistedPurchase
    ? '✅ Can help with purchases | ❌ Cannot withdraw/convert/transfer'
    : '⚠️ All financial actions disabled'

  return (
    <div
      style={{
        position: 'fixed',
        top: '35px',
        left: 0,
        right: 0,
        zIndex: 9999,
        backgroundColor: bgColor,
        borderBottom: `3px solid ${borderColor}`,
        padding: '12px 24px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
      }}
    >
      <div
        style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px'
        }}
      >
        {/* Left side - Info */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div
            style={{
              fontSize: '24px',
              animation: 'pulse 2s infinite'
            }}
          >
            {icon}
          </div>
          <div>
            <div
              style={{
                color: 'white',
                fontWeight: 'bold',
                fontSize: '16px',
                marginBottom: '2px'
              }}
            >
              {title}
            </div>
            <div
              style={{
                color: isAssistedPurchase ? '#DBEAFE' : '#FEE2E2',
                fontSize: '13px'
              }}
            >
              User: <strong>{session.targetEmail}</strong> |
              Admin: <strong>{session.adminEmail}</strong> |
              Duration: <strong>{duration}</strong>
            </div>
          </div>
        </div>

        {/* Right side - Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              backgroundColor: isAssistedPurchase ? '#1E40AF' : '#991B1B',
              color: 'white',
              padding: '6px 12px',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '600'
            }}
          >
            {statusMessage}
          </div>
          <button
            onClick={handleExit}
            style={{
              backgroundColor: 'white',
              color: bgColor,
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = isAssistedPurchase ? '#DBEAFE' : '#FEE2E2'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'white'
            }}
          >
            Exit Assistance Mode
          </button>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </div>
  )
}

/**
 * Wrapper to add padding when banner is visible
 */
export const MasterAdminViewWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [hasBanner, setHasBanner] = useState(false)

  useEffect(() => {
    const session = getMasterAdminViewSession()
    setHasBanner(!!session)
  }, [])

  return (
    <>
      <MasterAdminViewBanner />
      <div style={{ paddingTop: hasBanner ? '80px' : '0' }}>
        {children}
      </div>
    </>
  )
}

